import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faFileInvoice, faCalendarAlt, faHandshake, faFileAlt, faBox } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { dashboardService } from '../../services/dashboardService';
import { useUserInfo } from '../../contexts/UserContext';
import { resolveIssuerName } from '../../utils/issuerDisplay';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import './OutgoingInvoice.css';

/**
 * Debit Note Screen 1 - Header Information
 * Per PM mockup: Similar to Credit Note but for debit scenarios
 */

const DebitNoteScreen1 = ({ debitNoteData, setDebitNoteData, setLineItems, setVesselInfo, onNext, isReadOnly, issuerName = null, isPersisted = false }) => {
  const [templateTypes, setTemplateTypes] = useState([]);
  const [incomingInvoices, setIncomingInvoices] = useState([]);
  // null when the picker's list loaded. Otherwise why it did not -- refused,
  // truncated, or broken. See loadDropdowns.
  const [invoiceLoadProblem, setInvoiceLoadProblem] = useState(null);
  const [sessionUserName, setSessionUserName] = useState('');
  
  // Get current user info
  const { userInfo, updateUserInfo } = useUserInfo();
  
  useEffect(() => {
    loadDropdowns();
    loadIssuerInfo();
  }, []);
  
  const loadIssuerInfo = async () => {
    try {
      // Try from context first
      if (userInfo.first_name && userInfo.last_name) {
        setSessionUserName(`${userInfo.first_name} ${userInfo.last_name}`.trim());
      } else {
        // Fetch from API if context is empty (page reload)
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
        setSessionUserName(`${userInfoData.first_name} ${userInfoData.last_name}`.trim());
      }
    } catch (error) {
      console.error('Error loading issuer info:', error);
      setSessionUserName('Automatic (person creating the debit note)');
    }
  };

  // The Issuer names whoever RAISED this document, which is not whoever is
  // looking at it -- only an unsaved document belongs to the person typing.
  const issuerFullName = resolveIssuerName({
    isPersisted,
    storedIssuerName: issuerName,
    sessionUserName,
  });
  
  /**
   * Fill the two dropdowns this screen needs.
   *
   * The invoice list is loaded SEPARATELY from the templates, and its failure
   * is put on the screen. Both used to sit in one try/catch that logged to the
   * console and stopped -- so a refusal, a broken connection or a half-loaded
   * page all produced the same thing: a picker with nothing in it. Finance then
   * reads "-- Select Incoming Invoice --" and no options and concludes there
   * are no approved supplier invoices to debit, which is a different and much
   * more expensive answer than "we could not load them". An empty picker must
   * only ever mean empty.
   */
  const loadDropdowns = async () => {
    try {
      const templates = await invoicingService.fetchTemplateTypeOptions();
      setTemplateTypes(templates);
    } catch (error) {
      console.error('Error loading debit note templates:', error);
    }

    try {
      setInvoiceLoadProblem(null);
      const invoices = await invoicingService.fetchIncomingInvoices({ status: 'APPROVED,SENT,PAID,OVERDUE' });
      setIncomingInvoices(invoices.results || invoices);
    } catch (error) {
      console.error('Error loading invoices to debit:', error);
      setIncomingInvoices([]);
      setInvoiceLoadProblem(
        invoicingService.describeListFailure(error, 'The invoices you can debit'),
      );
    }
  };
  
  const handleInvoiceSelect = async (invoiceId) => {
    if (!invoiceId) return;
    
    try {
      const prefillData = await invoicingService.createDebitNoteFromInvoice(invoiceId);
      
      // Update header data
      setDebitNoteData(prev => ({
        ...prev,
        ...prefillData
      }));
      
      // Update line items if provided
      if (prefillData.line_items && setLineItems) {
        setLineItems(prefillData.line_items);
      }
      
      // Update vessel info if provided
      if (prefillData.vessel_info && setVesselInfo) {
        setVesselInfo(prefillData.vessel_info);
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
    }
  };
  
  // Helper function to parse internal reference into separate parts
  const parseInternalReference = (internalRef) => {
    if (!internalRef) return { delivery: '', contracts: '', salesRecaps: '' };
    
    const lines = internalRef.split('\n').map(line => line.trim()).filter(line => line);
    let delivery = '';
    let contracts = [];
    let salesRecaps = '';
    
    lines.forEach(line => {
      if (line.startsWith('Delivery:')) {
        delivery = line.replace('Delivery:', '').trim();
      } else if (line.includes('Contract:')) {
        contracts.push(line.replace('Seller Contract:', '').replace('Buyer Contract:', '').trim());
      } else if (line.startsWith('Sales Recaps:')) {
        salesRecaps = line.replace('Sales Recaps:', '').trim();
      }
    });
    
    return {
      delivery,
      contracts: contracts.join(', '),
      salesRecaps
    };
  };

  // Rebuild internal_reference from the three editable sub-fields while
  // preserving any other lines. Uses "Seller Contract:" so the value
  // round-trips through parseInternalReference.
  const updateInternalReference = (field, value) => {
    setDebitNoteData(prev => {
      const current = parseInternalReference(prev.internal_reference);
      current[field] = value;
      const extras = (prev.internal_reference || '')
        .split('\n').map(l => l.trim()).filter(Boolean)
        .filter(l => !l.startsWith('Delivery:') && !l.includes('Contract:') && !l.startsWith('Sales Recaps:') && !l.startsWith('Purchase Recaps:'));
      const lines = [];
      if (current.delivery) lines.push(`Delivery: ${current.delivery}`);
      if (current.contracts) lines.push(`Seller Contract: ${current.contracts}`);
      if (current.salesRecaps) lines.push(`Sales Recaps: ${current.salesRecaps}`);
      return { ...prev, internal_reference: [...lines, ...extras].join('\n') };
    });
  };
  
  return (
    <>
      <div className="invoice-screen-content">
        <div className="invoice-form-section">
          <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <FontAwesomeIcon icon={faBuilding} /> Debit Note Header & Partner Information
            </span>
            <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
              {debitNoteData.debit_note_number ? `Debit Note: ${debitNoteData.debit_note_number}` : '(number will be generated on save)'}
            </span>
          </h2>
          
          <div className="invoice-form-grid">
            {/* Left Column */}
            <div className="invoice-form-column">
              {/* Select Original Invoice */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faFileInvoice} /> Select Original Incoming Invoice
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {debitNoteData.original_invoice_number || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="original_invoice_select"
                    className="form-select"
                    value={debitNoteData.original_invoice || ''}
                    onChange={(e) => {
                      handleInvoiceSelect(e.target.value);
                      clearFieldError('original_invoice_select');
                    }}
                  >
                    <option value="">-- Select Incoming Invoice --</option>
                    {incomingInvoices.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.supplier_invoice_number || inv.hms_expense_number} - {inv.partner_name} - {inv.currency} {inv.gross_amount}
                      </option>
                    ))}
                  </select>
                )}
                {/* An empty picker with no explanation reads as "there is
                    nothing to debit". Say which it actually was. */}
                {invoiceLoadProblem && !isReadOnly && (
                  <div
                    role="alert"
                    className="field-load-problem"
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.6rem 0.8rem',
                      border: '1px solid #f59e0b',
                      borderLeft: '4px solid #b45309',
                      borderRadius: '4px',
                      background: '#fffbeb',
                      color: '#78350f',
                      fontSize: '0.85rem',
                    }}
                  >
                    <strong style={{ marginRight: '0.35rem' }}>
                      {invoiceLoadProblem.kind === 'truncated'
                        ? 'This picker is not showing every invoice.'
                        : 'The list of invoices could not be loaded.'}
                    </strong>
                    {invoiceLoadProblem.message}
                  </div>
                )}
              </div>
              
              {/* IMT Entity */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faBuilding} /> Entity
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={debitNoteData.hms_entity_name || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Issuer */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faUser} /> Issuer
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={issuerFullName}
                  readOnly
                  disabled
                />
              </div>
              
              {/* Original Invoice Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileInvoice} /> Original Invoice Number
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={debitNoteData.original_invoice_number || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Debit Note Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={debitNoteData.invoice_date || ''}
                  onChange={(e) => {
                    setDebitNoteData({ ...debitNoteData, invoice_date: e.target.value });
                    clearFieldError('invoice_date');
                  }}
                  name="invoice_date"
                  id="invoice_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} /> Debit Note Date
                    </span>
                  }
                  placeholder="Select debit note date"
                  required={true}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Internal Reference */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Internal Reference
                </label>
                <div className="internal-reference-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="delivery number"
                    value={parseInternalReference(debitNoteData.internal_reference).delivery}
                    onChange={(e) => updateInternalReference('delivery', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="contract numbers"
                    value={parseInternalReference(debitNoteData.internal_reference).contracts}
                    onChange={(e) => updateInternalReference('contracts', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="sales recap numbers"
                    value={parseInternalReference(debitNoteData.internal_reference).salesRecaps}
                    onChange={(e) => updateInternalReference('salesRecaps', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="invoice-form-column">
              {/* Partner Name */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faHandshake} /> Partner Name
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={debitNoteData.partner_name || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Debtor Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Debtor Number
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={debitNoteData.partner || ''}
                  readOnly
                  disabled
                  placeholder="from original invoice"
                />
              </div>
              
              {/* Debit Reason */}
              <div className="form-field-flexible">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Debit Reason
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {debitNoteData.debit_reason_notes || 'N/A'}
                  </div>
                ) : (
                  <textarea
                    className="form-textarea"
                    value={debitNoteData.debit_reason_notes || ''}
                    onChange={(e) => setDebitNoteData({ ...debitNoteData, debit_reason_notes: e.target.value })}
                    rows={4}
                    placeholder="Detailed explanation (interest calculation, additional charges, etc.)"
                  />
                )}
              </div>
              
              {/* Supply Reference */}
              <div className="form-field-flexible">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Supply Reference
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {debitNoteData.supply_reference || 'N/A'}
                  </div>
                ) : (
                  <textarea
                    className="form-textarea"
                    value={debitNoteData.supply_reference || ''}
                    onChange={(e) => setDebitNoteData({ ...debitNoteData, supply_reference: e.target.value })}
                    rows={3}
                    placeholder="text field"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="screen-navigation" style={{ justifyContent: 'flex-end' }}>
            <button className="btn-primary" type="button" onClick={onNext}>
              Next: Products <FontAwesomeIcon icon={faBox} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DebitNoteScreen1;
